import { useMemo } from 'react'

import {
  useFilters, useModals, useNumericParams, useSetState,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import type { AttachmentModel } from '@models/types'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditAttachmentModal from '@modals/CreateOrEditAttachmentModal'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditAttachmentModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditAttachmentModal: {
      closeModal: () => setState({ showCreateOrEditAttachmentModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }
  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditAttachmentModal } = state

  const { trainingLessonId } = useNumericParams()

  const { callbacks: filterCallbacks, defaultFilters } = useFilters()

  const modalContext = useModals({ setState })

  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditAttachmentModal: (payload: { attachment: AttachmentModel }) => {
        setModalData('CreateOrEditAttachmentModal', payload)
        setState({ showCreateOrEditAttachmentModal: true })
      },
      updateTrainingLesson: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent trainingLessonId={trainingLessonId} />

        <CreateOrEditAttachmentModal
          callbacks={callbacks('CreateOrEditAttachmentModal', setState)}
          showModal={showCreateOrEditAttachmentModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
